import NoodleAI from '@/components/Icons/NoodleAI';
import Workflow from '@/components/Icons/Workflow';
import Textbox from '@/components/Icons/Textbox';
import Database from '@/components/Icons/Database';
import Link from '@/components/Icons/Link';
import ConditionalFill from '@/components/Icons/ConditionalFill';
import classNames from 'classnames';
import Table from '@/components/Icons/Table';
import React, { FC, Fragment, ReactElement, useEffect, useState } from 'react';
import ArrowUpRight from '@components/Icons/ArrowUpRight';
import Spacer from '@/components/Spacer';
import FileText from '@components/Icons/FileText';
import { QuestionnaireResponse } from '@/typings/questionnaires-api-models';
import Tooltip from '@/components/Tooltip';
import ArrowCounterClockwise from '@components/Icons/ArrowCounterClockwise';
import { Questionnaire } from '@/typings/Questionnaire';
import { useEventsContext } from '@/providers/Events';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import ProgressIndicator from '@/components/ProgressIndicator';
import s from './PopulatedFrom.module.scss';

const ICON_PROPS = { color: 'var(--color-gray-75)', size: 12 };

type ExcludedSources = 'manual' | 'unknown';
export type AllowedPopulatedFromSources = Exclude<QuestionnaireResponse['source'], ExcludedSources>;

const typeConfig: Record<AllowedPopulatedFromSources, { icon: ReactElement; label: string }> = {
  'ai': {
    icon: <NoodleAI size={16} />,
    label: 'Populated by Ai',
  },
  'calculation': {
    icon: <ConditionalFill {...ICON_PROPS} />,
    label: 'Populated by Calculation',
  },
  'conditional-fill': {
    icon: <ConditionalFill {...ICON_PROPS} />,
    label: 'Populated by Conditional Autofill',
  },
  'credit-report': {
    icon: <Table {...ICON_PROPS} />,
    label: 'Populated from Credit Report',
  },
  'data-reference-slug': {
    icon: <Database {...ICON_PROPS} />,
    label: 'Populated from',
  },
  'field-template-key': {
    icon: <FileText {...ICON_PROPS} />,
    label: 'Populated from form data',
  },
  'field-template-list-id-map': {
    icon: <FileText {...ICON_PROPS} />,
    label: 'Populated from form data',
  },
  'initial-values': {
    icon: <Textbox {...ICON_PROPS} />,
    label: 'Default value',
  },
  'linked-field': {
    icon: <Link {...ICON_PROPS} />,
    label: 'Populated from Linked Field',
  },
  'workflow-context': {
    icon: <Workflow {...ICON_PROPS} />,
    label: 'Populated from Workflow Context',
  },
};

const PopulatedFrom: FC<{
  type?: AllowedPopulatedFromSources;
  onClick?: (e: React.MouseEvent) => void;
  prominent?: boolean;
  itemId?: string;
  tooltip?: string | null;
  canReset?:boolean;
  field?: Questionnaire.FieldCommon,
}> = ({ itemId, type, onClick, prominent , tooltip = null, canReset, field}) => {
  const { emit, addListener, removeListener } = useEventsContext();
  const isResetAutofillEnabled = useFeatureFlag('can-use-reset-autofill');
  const [isAutofillRerunning, setIsAutofillRerunning] = useState<boolean>(false);

  useEffect(() => {
    const listener = addListener('autofillRerunning', ({status, fieldId}) => {
      if(fieldId===field?.id) setIsAutofillRerunning(status);
    });
    return () => removeListener('autofillRerunning', listener);
  }, [addListener, removeListener]);

  if (!type) {
    return null;
  }

  const { icon, label } = typeConfig[type] || {};

  if (!icon || !label) {
    return null;
  }

  const Component = onClick ? 'button' : 'div';

  const TooltipOrFragment = tooltip
    ? ({ children }: { children: React.ReactElement }) => (
      <Tooltip text={tooltip}>
        {children}
      </Tooltip>
    )
    : Fragment;

  return (
    <div style={{display:"flex"}}>
      <TooltipOrFragment>
        <Component
          className={classNames(s.label, prominent && s.prominent)}
          onClick={onClick}
          style={{
            ...(onClick && { cursor: 'pointer' } || {}),
            ...((field && canReset)?{borderBottomRightRadius:0, borderTopRightRadius:0}:{}),
          }}
        >
          {icon} {label} {type === 'data-reference-slug' && itemId ? `${itemId.replace(/-/g, ' ')} dataset` : ''}
          {type === 'data-reference-slug' && (
            <>
              <div style={{ minWidth: 16, width: 16 }} />
              <div className={s.rightIcon}>
                <ArrowUpRight />
              </div>
            </>
          )}
          <Spacer size={8}/>
        </Component>
      </TooltipOrFragment>
      {field && canReset && isResetAutofillEnabled && !isAutofillRerunning
        ?<Tooltip text="Re-run autofill" >
          <div className={s.resetBtn} onClick={() => emit("reRunAutofill",{field})}>
            <ArrowCounterClockwise size={12} weight='fill' color="var(--color-gray-75)" />
          </div>
        </Tooltip>
        : null}
      {
        field && canReset && isResetAutofillEnabled && isAutofillRerunning
          ?<div className={s.loadingIndicator}>
            <ProgressIndicator size={17}/>
          </div>
          : null

      }
    </div>
  );
};

export default PopulatedFrom;
